import { get } from '@/common/request'
import AliOSS from 'ali-oss'
import { Toast } from 'vant'

export default {
  /**
   * 上传单个文件到阿里云OSS
   * @param {File} file - 要上传的文件对象
   * @returns {Promise<string>} - 返回上传成功的文件URL
   */
  async upload(file, bucket = 'maijiawei', region = 'oss-cn-shenzhen') {
    const sts = await get('/aliyun/sts', {
      filename: file.name,
      bucket,
      region
    })
    const client = new AliOSS({
      region: sts.region, // 地域
      accessKeyId: sts.AccessKeyId, // 访问密钥ID
      accessKeySecret: sts.AccessKeySecret, // 访问密钥秘密
      stsToken: sts.SecurityToken, // 安全令牌
      bucket: sts.bucket // 存储桶名称
    })
    const result = await client.put(sts.object, file)
    return result.url.replace('http://', 'https://') // 返回HTTPS URL
  },

  /**
   * 复制文本
   * @param {string} text - 要复制的文本
   * @param {boolean} showToast - 是否显示复制成功提示
   */
  copyText(text, showToast = true) {
    try {
      navigator.clipboard.writeText(text)
    } catch (err) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    if (showToast) {
      Toast.success('复制成功')
    }
  },

  /**
   * 拖拽处理器
   * @param {Konva.Stage} stage - 舞台
   * @param {Konva.Transformer} transformer - 变换器
   * @param {Function} onTransformChange - 变换变化回调
   * @param {Function} onSelect - 选择回调
   * @param {Function} getMultipleEnabledFunction - 获取多选模式的方法
   */
  transformerHandler(stage, transformer, onTransformChange, onSelect, getMultipleEnabledFunction) {
    // 初始化变量用于跟踪拖拽状态
    let startX = 0, // 起始X坐标
      startY = 0, // 起始Y坐标
      startPositions = {}, // 存储每个节点起始位置的映射
      endPositions = {}, // 存储每个节点最终位置的映射
      isMoved = false // 标记是否发生了移动

    // 监听变换器的事件
    transformer.on('transformend', (e) => {
      isMoved = true
      onTransformChange && onTransformChange()
    })

    // 监听舞台上的所有鼠标和触摸事件
    stage.on('touchstart touchmove touchend mousedown mousemove mouseup', (e) => {
      // 处理开始拖拽事件
      if (['touchstart', 'mousedown'].includes(e.type)) {
        // 获取触摸点或鼠标的坐标
        const { clientX: x, clientY: y } = e.evt?.touches?.[0] || e.evt
        startX = x
        startY = y
        startPositions = {}
        endPositions = {}
        isMoved = false

        // 记录所有选中节点的起始位置
        for (const node of transformer.nodes()) {
          startPositions[node.id()] = {
            x: node.x(),
            y: node.y()
          }
        }
      }
      // 处理拖拽移动事件
      else if (['touchmove', 'mousemove'].includes(e.type)) {
        const { clientX: x, clientY: y } = e.evt?.touches?.[0] || e.evt
        const dx = x - startX // 计算X轴移动距离
        const dy = y - startY // 计算Y轴移动距离
        isMoved = true

        // 更新所有选中节点的位置
        for (const node of transformer.nodes()) {
          const attrs = startPositions[node.id()]
          if (attrs) {
            node.x(attrs.x + dx)
            node.y(attrs.y + dy)
            endPositions[node.id()] = {
              x: node.x(),
              y: node.y()
            }
          }
        }
      }
      // 处理拖拽结束事件
      else if (['touchend', 'mouseup'].includes(e.type)) {
        // 如果没有移动且点击了目标，处理选择逻辑
        if (isMoved === false && e?.target) {
          let currentNode = e.target
          let targetNode = null

          // 向上查找可拖拽的父节点
          while (currentNode) {
            if (currentNode.getAttr('_draggable')) {
              targetNode = currentNode
              break
            }
            currentNode = currentNode.parent
          }

          if (targetNode) {
            // 更新选中节点
            const multipleEnabled = (getMultipleEnabledFunction && getMultipleEnabledFunction()) || false
            if (multipleEnabled) {
              // 多选模式：如果已选中则移除，否则添加
              const currentNodes = transformer.nodes()
              if (currentNodes.includes(targetNode)) {
                transformer.nodes(currentNodes.filter((node) => node !== targetNode))
              } else if (targetNode) {
                transformer.nodes([...currentNodes, targetNode])
              }
            } else {
              // 单选模式：如果已选中则清空，否则选中当前节点
              const currentNodes = transformer.nodes()
              if (currentNodes.includes(targetNode)) {
                transformer.nodes([])
              } else if (targetNode) {
                transformer.nodes([targetNode])
              }
            }
          } else {
            transformer.nodes([])
          }
        }

        if (Object.keys(endPositions).length > 0) {
          onTransformChange && onTransformChange()
        }

        onSelect && onSelect(transformer.nodes().map((node) => node.id()))

        // 清理状态
        startPositions = {}
        endPositions = {}
      }
    })
  },

  /**
   * 获取图片非透明区域并裁剪，返回裁剪后的 Image 对象和边界信息
   * @param {HTMLImageElement} image - 要处理的图片
   * @returns {Promise<{ image: HTMLImageElement, boundingBox: { x: number, y: number, width: number, height: number } }>}
   */
  async cropNonTransparentImage(image) {
    // 创建画布并绘制图片
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = image.width
    canvas.height = image.height
    ctx.drawImage(image, 0, 0)

    // 查找非透明区域
    let minX = image.width,
      minY = image.height,
      maxX = 0,
      maxY = 0
    const imageData = ctx.getImageData(0, 0, image.width, image.height)
    const data = imageData.data
    for (let y = 0; y < image.height; y++) {
      for (let x = 0; x < image.width; x++) {
        const index = (y * image.width + x) * 4
        if (data[index + 3] > 0) {
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }
    }
    // 计算边界
    const box = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    }

    // 如果没有非透明区域，返回空白图片
    if (box.width <= 0 || box.height <= 0) {
      const emptyCanvas = document.createElement('canvas')
      emptyCanvas.width = 1
      emptyCanvas.height = 1
      return new Promise((resolve) => {
        const img = new window.Image()
        img.onload = () => {
          resolve({
            image: img,
            url: emptyCanvas.toDataURL('image/png'),
            boundingBox: { x: 0, y: 0, width: 0, height: 0 }
          })
        }
        img.src = emptyCanvas.toDataURL('image/png')
      })
    }

    // 裁剪非透明区域
    const cropCanvas = document.createElement('canvas')
    const cropCtx = cropCanvas.getContext('2d')
    cropCanvas.width = box.width
    cropCanvas.height = box.height
    cropCtx.drawImage(image, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height)
    return new Promise((resolve) => {
      const img = new window.Image()
      img.onload = () => {
        resolve({
          image: img,
          url: cropCanvas.toDataURL('image/png'),
          boundingBox: box
        })
      }
      img.src = cropCanvas.toDataURL('image/png')
    })
  },

  /**
   * 将Base64字符串转换为File对象
   * @param {string} base64Url - Base64字符串
   * @returns {File} - File对象
   */
  base64ToFile(base64Url) {
    // base64 格式: data:[<mime-type>];base64,<data>
    let arr = base64Url.split(',')
    let mime = arr[0].match(/:(.*?);/)[1] // 提取 mime 类型
    let bstr = atob(arr[1]) // 将 Base64 解码
    let n = bstr.length
    let u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n) // 将字符串转换为字节
    }

    // 根据 mime 类型生成文件扩展名
    let extension = 'bin'
    switch (mime) {
      case 'image/jpeg':
        extension = 'jpg'
        break
      case 'image/png':
        extension = 'png'
        break
      case 'image/gif':
        extension = 'gif'
        break
      case 'application/pdf':
        extension = 'pdf'
        break
      case 'text/plain':
        extension = 'txt'
        break
      default:
        extension = 'bin' // 未知类型，返回默认扩展名
    }

    // 自动生成文件名
    let filename = `file_${Date.now()}.${extension}`

    // 创建并返回 File 对象
    return new File([u8arr], filename, { type: mime })
  },

  /**
   * 将文件或Blob对象转换为Base64字符串
   * @param {File | Blob} fileOrBlob - 要转换的文件或Blob对象
   * @returns {Promise<string>} - 返回Base64字符串
   */
  fileOrBlobToBase64(fileOrBlob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (event) => {
        const base64String = event.target?.result
        resolve(base64String)
      }
      reader.onerror = (error) => {
        reject(error)
      }
      reader.readAsDataURL(fileOrBlob)
    })
  },
  /**
   * 将 Blob 对象转换为 File 对象
   * @param {Blob} blob - 要转换的 Blob 对象
   * @returns {File} - 返回 File 对象
   */
  blobToFile(blob) {
    return new File([blob], blob.name, { type: blob.type })
  },

  /**
   * 加载图片
   */
  async loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.src = url
      image.crossOrigin = 'Anonymous'
      image.onload = () => resolve(image)
      image.onerror = reject
    })
  },

  /**
   * 填充字幕间隙
   * @param {Array} subtitle 字幕
   * @returns {Array} 填充后的字幕
   */
  fillSubtitleGaps(subtitle, audioDuration) {
    const newSubtitle = []

    for (let i = 0; i < subtitle.length; i++) {
      const item = subtitle[i]
      const nextItem = i < subtitle.length - 1 ? subtitle[i + 1] : null

      // 处理开头空白
      if (i === 0 && item.startTime > 0) {
        newSubtitle.push({
          text: '',
          startTime: 0,
          endTime: item.startTime,
          speaker: '',
          words: []
        })
      }

      // 添加当前字幕项
      newSubtitle.push({
        ...item
      })

      // 处理中间空白
      if (nextItem && nextItem.startTime - item.endTime > 0) {
        newSubtitle.push({
          text: '',
          startTime: item.endTime,
          endTime: nextItem.startTime,
          speaker: '',
          words: []
        })
      }

      // 处理结尾空白
      if (i === subtitle.length - 1 && item.endTime < audioDuration) {
        newSubtitle.push({
          text: '',
          startTime: item.endTime,
          endTime: audioDuration,
          speaker: '',
          words: []
        })
      }
    }

    // 合并时长小于0.3的字幕项
    for (let i = newSubtitle.length - 1; i > 0; i--) {
      const currentItem = newSubtitle[i]
      const prevItem = newSubtitle[i - 1]

      // 如果当前项时长小于0.3秒，将其合并到前一项
      if (currentItem.endTime - currentItem.startTime < 0.3 && currentItem.text === '') {
        prevItem.endTime = currentItem.endTime
        newSubtitle.splice(i, 1)
      }
    }

    return newSubtitle
  },

  /**
   * 删除空字幕并调整时间
   */
  removeEmptySubtitlesAndAdjustTime(subtitles) {
    let i = 0
    while (i < subtitles.length) {
      const subtitle = subtitles[i]
      if (subtitle.text === '') {
        // 记录当前startTime和endTime
        const currentStartTime = subtitle.startTime
        const currentEndTime = subtitle.endTime
        // 删除当前项
        subtitles.splice(i, 1)
        // 查找下一个text不为空的项，设置其startTime
        let found = false
        for (let j = i; j < subtitles.length; j++) {
          if (subtitles[j].text !== '') {
            subtitles[j].startTime = currentStartTime
            found = true
            break
          }
        }
        // 如果没有找到下一个非空项，则将前一个非空项的endTime设置为被删除项的endTime
        if (!found && i - 1 >= 0) {
          for (let k = i - 1; k >= 0; k--) {
            if (subtitles[k].text !== '') {
              subtitles[k].endTime = currentEndTime
              break
            }
          }
        }
        // 删除后不自增i，继续检查当前位置
      } else {
        i++
      }
    }
    return subtitles
  },

  download(url, type) {
    let func = uni.navigateTo
    if (window.navigator.userAgent.indexOf('miniProgram') > -1) {
      func = wx.miniProgram.navigateTo
    }
    func({
      url: `/pages/download/download?type=${type}&url=${encodeURIComponent(url)}`
    })
  },

  to(url) {
    if (url.startsWith('http')) {
      window.location.href = url
    } else if (url.startsWith('/h5/')) {
      router.push(url.substring(3))
    } else if (url.startsWith('/pages/')) {
      let func = uni.navigateTo
      if (window.navigator.userAgent.indexOf('miniProgram') > -1) {
        func = wx.miniProgram.navigateTo
      }
      if (['/pages/index/index', '/pages/draft/draft', '/pages/user/user'].includes(url)) {
        func = uni.switchTab
        if (window.navigator.userAgent.indexOf('miniProgram') > -1) {
          func = wx.miniProgram.switchTab
        }
      }
      func({
        url: url
      })
    }
  }
}
